import { useMemo } from 'react';
import { useLocation } from '@reach/router';
import { useQueryFilter } from 'hooks/useQueryFilter';
import { CVEsInterface, FileResponseInterface, SandboxResponseInterface, VulnerabilityDataInterface } from 'services/interface';
import { useSWR, useSWRImmutable, useSWRInfinite } from 'services/swr';
import { getFileProgress } from 'utils/Format';

export const useFileData = ({ dataId }: { dataId?: string | null; } = {}) => {
    const { pathname } = useLocation();
    const segments = pathname.split('/');
    const type = segments[2];
    const id = segments[3];

    const resultsKey = useMemo(() => {
        if (typeof dataId === 'string') {
            return { url: `/v4/file/${dataId}` };
        }

        if (dataId === null) {
            return null;
        }

        if (id && type === 'file') {
            return { url: `/v4/file/${id}` };
        }

        if (id && type === 'hash') {
            return { url: `/v4/hash/${id}` };
        }

        return null;
    }, [id, type, dataId]);

    const fileRefreshInterval = (data?: FileResponseInterface) => {
        const fileProgress = getFileProgress(data?.process_info?.progress_percentage, data?.scan_results?.progress_percentage);

        if (fileProgress === 100 || type === 'hash') {
            return 0;
        }

        return 3000;
    };


    return useSWR<FileResponseInterface>(resultsKey, {
        refreshInterval: fileRefreshInterval,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
};

export const useSandboxData = () => {
    const { data: fileData } = useFileData();
    const { lastSandboxId } = useQueryFilter();
    const sandboxId = lastSandboxId || fileData?.sandbox_id;
    const sandboxResponseKey = sandboxId ? { url: `/v4/sandbox/${sandboxId}` } : null;

    const sandboxRefreshInterval = (data?: SandboxResponseInterface) => {
        if (data?.scan_in_progress) {
            return 10000;
        }
        if (data?.final_verdict || data?.scan_results?.progress_percentage === 100 || data?.scan_results?.scan_all_result_a === 'Aborted') {
            return 0;
        }
        return 3000;
    };

    return useSWR<SandboxResponseInterface>(sandboxResponseKey, {
        refreshInterval: sandboxRefreshInterval,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
};

export const useSandboxReport = () => {
    const { data: sandboxData } = useSandboxData();
    const sandboxDataKey = sandboxData?.store_at ? { url: sandboxData.store_at } : null;

    return useSWRImmutable(sandboxDataKey);
};

export const useVulnerabilityData = () => {
    const { data: fileData } = useFileData();
    const VULNERABILITY_LIMIT = 10;

    const hasVulnerability = fileData?.additional_info?.includes('vulnerability');
    const fileHash = fileData?.file_info?.sha1;

    const getVulnerabilityDataKey = (index: number, prevPageData: VulnerabilityDataInterface) => {
        const pageLength = prevPageData?.data?.length ?? 0;
        const offset = index === 0 ? pageLength : index * pageLength;

        if (!(hasVulnerability && fileHash)) {
            return null;
        }

        if (index > 0 && pageLength < VULNERABILITY_LIMIT) {
            return null;
        }

        return { url: `/v4/vulnerability/${fileHash}?limit=${VULNERABILITY_LIMIT}&offset=${offset}` };
    };

    const vulnerability = useSWRInfinite<VulnerabilityDataInterface>(getVulnerabilityDataKey, { revalidateFirstPage: false, revalidateIfStale: false, revalidateOnFocus: false, revalidateOnReconnect: false });

    const data = (vulnerability.data ?? []).reduce((accumulator: Array<CVEsInterface>, value) => {
        if (value?.data) {
            return [...accumulator, ...value.data];
        }
        return [...accumulator];
    }, []);

    return { ...vulnerability, data };
};
